import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, Loader2, Package } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, newKey, rp, todayJkt } from "@/lib/api";
import { Field, PageHeader, QtyInput, RupiahInput, SectionTitle } from "@/components/common";
import { History } from "@/components/History";

export default function Pengeluaran() {
  const { ws, bump } = useAuth();
  const empty = { date: todayJkt(), category: "Kedelai", note: "", amount: 0, qty: "", toStock: true };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const key = useRef(newKey());
  const matKey = ws.category_material[f.category];
  const mat = ws.materials.find((m) => m.key === matKey);
  const isMat = !!mat && f.toStock;

  const save = async () => {
    if (!f.amount) { toast.error("Isi nominal yang dibayar."); return; }
    if (isMat && !(Number(f.qty) > 0)) { toast.error("Isi jumlah bahan yang diterima."); return; }
    setBusy(true);
    try {
      await api.post("/expense", { date: f.date, category: f.category, note: f.note, amount: f.amount,
        material: isMat ? matKey : null, qty: isMat ? Number(f.qty) : 0, client_key: key.current });
      toast.success(isMat ? `Pengeluaran ${rp(f.amount)} tersimpan. Stok ${mat.name} bertambah ${f.qty} ${mat.unit}.` : `Pengeluaran ${rp(f.amount)} tersimpan.`);
      setF({ ...empty, date: f.date, category: f.category }); key.current = newKey(); setReload((x) => x + 1); bump();
    } catch (e) { toast.error(errMsg(e)); if (e?.response?.status === 409) key.current = newKey(); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="pengeluaran-page">
      <PageHeader icon={Receipt} color="#d9a21b" title="Pengeluaran" subtitle="Catat uang yang benar-benar sudah dibayar." />
      <div className="grid xl:grid-cols-[1fr_1fr] gap-6">
        <div className="jt-card p-5 sm:p-6 space-y-4 self-start">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Tanggal"><input type="date" className="jt-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="expense-date-input" /></Field>
            <Field label="Kategori">
              <select className="jt-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value, toStock: true })} data-testid="expense-category-select">
                {ws.categories.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          {mat && (
            <div className="rounded-2xl border border-[#c8e0cc] bg-[#f1f8f1] p-4 space-y-3" data-testid="expense-material-section">
              <label className="flex items-center gap-2.5 text-sm font-semibold cursor-pointer">
                <input type="checkbox" className="w-5 h-5 accent-[#1f7a46]" checked={f.toStock} onChange={(e) => setF({ ...f, toStock: e.target.checked })} data-testid="expense-to-stock-checkbox" />
                <Package className="w-4 h-4 text-[#1f7a46]" /> Ini pembelian bahan baku (stok bertambah otomatis)
              </label>
              {f.toStock && (
                <div className="grid grid-cols-3 gap-2.5">
                  <Field label="Nama Bahan"><input className="jt-input" value={mat.name} disabled data-testid="expense-material-name" /></Field>
                  <Field label="Jumlah"><QtyInput value={f.qty} onChange={(v) => setF({ ...f, qty: v })} testid="expense-qty-input" /></Field>
                  <Field label="Satuan"><input className="jt-input" value={mat.unit} disabled data-testid="expense-unit" /></Field>
                </div>
              )}
            </div>
          )}
          <Field label={isMat ? "Total Harga (Rp)" : "Nominal (Rp)"}><RupiahInput value={f.amount} onChange={(v) => setF({ ...f, amount: v })} testid="expense-amount-input" /></Field>
          <Field label="Keterangan"><input className="jt-input" placeholder="Contoh: beli di pasar" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="expense-note-input" /></Field>
          <p className="text-xs text-muted-foreground">Bahan diterima tapi belum dibayar penuh? Catat di <Link to="/kasbon-pabrik" className="font-bold text-[#2357c9]">Kasbon Pabrik</Link> — uang muka yang dibayar otomatis masuk ke riwayat Pengeluaran.</p>
          <button className="jt-btn jt-btn-primary w-full" disabled={busy} onClick={save} data-testid="expense-save-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Simpan Pengeluaran</button>
        </div>
        <div className="jt-card p-5 sm:p-6">
          <SectionTitle>Riwayat Pengeluaran</SectionTitle>
          <History kinds="expense,sup_payment" reloadKey={reload} onChanged={bump} testid="expense-history" />
        </div>
      </div>
    </div>
  );
}
